import { useMemo } from "react";
import { buildCompositionFlowGeometry } from "../../lib/flowGeometry.js";

// CompositionFlowOverlay — the sankey flow ribbons between composition columns,
// in the scope-columns flow language (drawFlowLinks): thin green S-curves with a
// surface-colored halo interleaved PER curve so they carve at crossings,
// attaching to small node bars where the flows stack proportionally. An
// absolutely-positioned SVG over the triptych — it draws only in the gutters, so
// the in-row sparklines are untouched.
//
// The halo is --panel (the Sheet's own surface), NOT --plot-bg: this overlay
// sits directly on the composition Sheet (--panel-soft), which is LIGHTER than
// --plot-bg/--bg. A bg-colored halo there paints darker than the surface and
// reads as a dark smudge instead of carving invisibly. (The Position overlay
// keeps --plot-bg — it sits on an inner --plot-bg box.)
//
// Flows are shown for EVERYTHING visible (no cap). Legibility comes from the same
// hover auto-dimming the scope columns use: with no row hovered every link sits
// at a quiet base alpha; hover a row and the links touching it pop to full while
// the rest fall back — so a dense bundle stays readable without hiding anything.

const BASE_LINK = 0.22;
const SEL_LINK = 0.68;
const DIM_LINK = 0.08;
const BASE_NODE = 0.44;
const SEL_NODE = 0.85;
const DIM_NODE = 0.22;

/**
 * @param {number} width
 * @param {number} height
 * @param {Array<{leftX, rightX, rows: Array<{y, value}>}>} columnGeom  per column, in order
 * @param {Array<{pair, aRow, bRow, count}>} flows
 * @param {{col:number, row:number}|null} hovered  the focused composition cell (shown-column idx, row idx)
 */
export default function CompositionFlowOverlay({ width, height, columnGeom, flows, hovered }) {
  // Geometry (paths + nodes) is hover-independent — only the alpha changes on
  // hover, so this memo doesn't recompute on mouse move. The math lives in
  // flowGeometry.js so it can be unit-tested without React/DOM.
  const { paths, nodes } = useMemo(
    () => buildCompositionFlowGeometry(width, height, columnGeom, flows),
    [width, height, columnGeom, flows],
  );

  if (!paths.length) return null;

  const hasFocus = hovered != null && hovered.col != null && hovered.row != null;
  // Only defined under focus, so it never dereferences a null `hovered`.
  const touches = hasFocus
    ? (p) => (p.pair === hovered.col && p.aRow === hovered.row) || (p.pair + 1 === hovered.col && p.bRow === hovered.row)
    : null;
  // Nodes that a focused link terminates on stay lit; the rest dim.
  const lit = new Set();
  if (hasFocus) for (const p of paths) if (touches(p)) { lit.add(`${p.pair}:${p.aRow}`); lit.add(`${p.pair + 1}:${p.bRow}`); }

  // Dim (or base) layer first, focused layer last — so focused halos carve
  // through the dimmed ones, matching drawFlowLinks' two-pass order.
  const back = hasFocus ? paths.filter((p) => !touches(p)) : paths;
  const front = hasFocus ? paths.filter(touches) : [];
  // Key by stable path identity (NOT subset index or alpha): a path only moves
  // between the back/front lists on hover — same key → React reorders + updates
  // alpha instead of unmount/remount, so there's no flash on hover enter/leave.
  const curve = (p, alpha) => (
    <g key={`${p.pair}-${p.aRow}-${p.bRow}`}>
      <path d={p.d} fill="none" stroke="var(--panel)" strokeWidth={p.sw + 2.4} strokeLinecap="round" />
      <path d={p.d} fill="none" stroke="var(--green)" strokeOpacity={alpha} strokeWidth={p.sw} strokeLinecap="round" />
    </g>
  );

  return (
    <svg className="pointer-events-none absolute inset-0 z-0" width={width} height={height} style={{ overflow: "visible" }} aria-hidden="true">
      {back.map((p) => curve(p, hasFocus ? DIM_LINK : BASE_LINK))}
      {front.map((p) => curve(p, SEL_LINK))}
      {nodes.map((n, i) => {
        const key = `${n.col}:${n.row}`;
        const highlighted = hasFocus && n.col === hovered.col && n.row === hovered.row;
        const alpha = hasFocus ? (highlighted ? SEL_NODE : (lit.has(key) ? BASE_NODE : DIM_NODE)) : BASE_NODE;
        return <rect key={`n${i}`} x={n.x} y={n.y} width={n.w} height={n.h} rx="1" fill="var(--green)" fillOpacity={alpha} />;
      })}
    </svg>
  );
}
